// keep this imports at top
import { loadPolyfills } from './polyfill-loader';
// ready for PWA
// import * as serviceWorker from './serviceWorker';

import ReactDOM from 'react-dom/client';

import { configureLocale, setLocale } from '@tmapy/intl';
import { deriveAuthFromConfig } from '@tmapy/auth';
import {
  AUTHENTICATION_LOGOUT_START,
  configureAuth,
  initCobraAuth,
  authGlobals,
} from '@tmapy/auth';
import { Icon, SvgSpinner } from '@tmapy/style-guide';

import { CenterLayout } from './app/components/CenterLayout';
import { App } from './app/App';
import { configurePersistStore } from './redux';
import { VERSION } from './constants';
import { loadConfig, setConfigToStore } from './app/actions';
import { loadLocale } from './locale/loadLocale';
import {
  twistLogin,
  actionTwistTokenRefresh,
  twistLogout,
  cobraLogin,
  actionFetchPolicies,
} from './auth/actions';
import { LOCALSTORAGE_REFRESH_TOKEN_KEY } from './auth/constants';
import { auth, authInitialState } from './auth/reducers/auth';
import { sentryInit } from './sentry';
import {
  setCheckListFilter,
  setDocListFilter,
  setGeodesistsListFilter,
  setOrderListFilter,
  setPriceListItemListFilter,
  setPublishsetListFilter,
  setSubjectListFilter,
  setUserListFilter,
} from './redux/filter/actions';
import { migrateRenamedDocStates } from './redux/filter/migrateRenamedDocStates';
import { initRouteColapsible, setMenuRouteFilterId } from './redux/menu/actions';

console.log(`version: ${VERSION} (${process.env.NODE_ENV})`);

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://bit.ly/CRA-PWA
// serviceWorker.unregister();

async function main() {
  await loadPolyfills();

  sentryInit();

  const rootElement = document.getElementById('root');
  if (!rootElement) {
    throw new Error("Root element with id 'root' not found.");
  }
  const root = ReactDOM.createRoot(rootElement);

  root.render(
    <CenterLayout>
      <Icon element={<SvgSpinner />} size="med" />
    </CenterLayout>,
  );

  window.DMX_CONFIG = await loadConfig();

  // FILTER DOCS
  const localStorageFilters = migrateRenamedDocStates(
    JSON.parse(window.localStorage.getItem('filter') || '{}'),
  );
  // MENU
  const localStorageMenu = JSON.parse(window.localStorage.getItem('menu') || '{}');

  // AUTH
  const hasExternalLogin = !globalThis.DMX_CONFIG.auth && !!globalThis.DMX_CONFIG.loginUrl;
  const authConfig = globalThis.DMX_CONFIG.auth
    ? deriveAuthFromConfig(globalThis.DMX_CONFIG.auth)
    : undefined;

  if (!authConfig && !hasExternalLogin) {
    throw new Error('The configuration is without authentication settings.');
  }
  configureAuth(authConfig!, authInitialState, auth as any);

  const store = await configurePersistStore();
  store.dispatch(setConfigToStore(globalThis.DMX_CONFIG));

  // INTL
  store.dispatch(
    configureLocale({
      loader: loadLocale,
      supported: [window.DMX_CONFIG.lang ?? 'cs'],
    }),
  );

  const locale = store.getState()?.intl?.currentLocale;

  if (globalThis.DMX_CONFIG.auth) {
    const isLoggedIn = await initCobraAuth(store.dispatch);
    if (!isLoggedIn && store.getState().auth.isLoggedIn) {
      // if not actually logged in, simulate logout
      store.dispatch({ type: AUTHENTICATION_LOGOUT_START });
    }

    const token = store.getState().auth.access_token;
    if (!token) {
      await store.dispatch(actionFetchPolicies() as any);
    }
    await Promise.all([
      // Run in parallel
      token && store.dispatch(cobraLogin(token) as any),
      store.dispatch(setLocale(locale) as any),
    ]);
  } else {
    await store.dispatch(setLocale(locale) as any);

    const params = new URLSearchParams(window.location.search);
    const oneTimeToken = params.get('oneTimeToken');
    if (oneTimeToken) {
      await store.dispatch(twistLogin(oneTimeToken) as any);
    } else {
      authGlobals.refresh_token = window.localStorage.getItem(LOCALSTORAGE_REFRESH_TOKEN_KEY);
      if (authGlobals.refresh_token) {
        if (!(await store.dispatch(actionTwistTokenRefresh() as any))) {
          store.dispatch(twistLogout() as any);
        }
      } else {
        await store.dispatch(twistLogout() as any);
      }
    }
  }

  // FILTER DOCS
  if (localStorageFilters.docList) {
    store.dispatch(setDocListFilter(localStorageFilters.docList));
  }
  if (localStorageFilters.checkList) {
    store.dispatch(setCheckListFilter(localStorageFilters.checkList));
  }
  if (localStorageFilters.orderList) {
    store.dispatch(setOrderListFilter(localStorageFilters.orderList));
  }
  if (localStorageFilters.geodesistsList) {
    store.dispatch(setGeodesistsListFilter(localStorageFilters.geodesistsList));
  }
  if (localStorageFilters.publishsetList) {
    store.dispatch(setPublishsetListFilter(localStorageFilters.publishsetList));
  }
  if (localStorageFilters.priceListItemList) {
    store.dispatch(setPriceListItemListFilter(localStorageFilters.priceListItemList));
  }
  if (localStorageFilters.subjectList) {
    store.dispatch(setSubjectListFilter(localStorageFilters.subjectList));
  }
  if (localStorageFilters.userList) {
    store.dispatch(setUserListFilter(localStorageFilters.userList));
  }

  // MENU
  const config = store.getState()?.app.config;
  const configMenuCollapse = { ...config.manager?.menuCollapse, ...config.vydej?.menuCollapse };
  if (localStorageMenu.menuRouteFilterId) {
    store.dispatch(setMenuRouteFilterId(localStorageMenu.menuRouteFilterId));
  }
  if (localStorageMenu.collapsible && Object.keys(localStorageMenu.collapsible).length) {
    store.dispatch(initRouteColapsible(localStorageMenu.collapsible));
  } else if (Object.keys(configMenuCollapse).length) {
    store.dispatch(initRouteColapsible(configMenuCollapse));
  }

  root.render(<App />);
}

main();
